import { Typography, theme } from "antd";
import PageLayout from "./PageLayout";

const { Title, Paragraph } = Typography;
const { useToken } = theme;

const PrivacyPolicy = () => {
	const { token } = useToken();

	return (
		<PageLayout>
			<div
				style={{
					maxWidth: "800px",
					margin: "120px auto 60px",
					padding: "0 24px",
				}}
			>
				<Title style={{ color: token.colorText, marginBottom: "8px" }}>
					Privacy Policy
				</Title>
				<Paragraph
					style={{ color: token.colorTextSecondary, marginBottom: "40px" }}
				>
					Last updated: August 31, 2026
				</Paragraph>

				<Title level={3} style={{ color: token.colorText }}>
					1. Introduction
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					BodhiStreams ("we", "our", or "us") operates the BodhiStreams mobile
					application and website (bodhistreams.com). This Privacy Policy
					explains how we collect, use, and protect your personal information
					when you use our services.
				</Paragraph>

				<Title level={3} style={{ color: token.colorText }}>
					2. Information We Collect
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					We collect the following types of information:
				</Paragraph>
				<ul style={{ color: token.colorTextSecondary, marginBottom: "16px" }}>
					<li>
						<strong>Account information:</strong> When you create an account, we
						collect your name, email address, and authentication credentials
						(including via Google Sign-In).
					</li>
					<li>
						<strong>Usage data:</strong> We collect information about how you
						interact with our app, including listening history, playlists, and
						preferences.
					</li>
					<li>
						<strong>Device information:</strong> We collect device type,
						operating system version, and unique device identifiers for app
						functionality and troubleshooting.
					</li>
					<li>
						<strong>Payment information:</strong> Subscription payments are
						processed by Stripe. We do not store your full credit card details
						on our servers.
					</li>
					<li>
						<strong>Assistant conversations:</strong> If you chat with our
						in-app assistant, we collect and store the messages you send so we
						can respond to you and improve the quality of the assistance.
					</li>
				</ul>

				<Title level={3} style={{ color: token.colorText }}>
					3. Information We Do Not Collect
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					BodhiStreams does <strong>not</strong> access, collect, or store any
					audio recordings from your device. Our app does not use your
					microphone. We do not collect location data, contacts, or any other
					sensitive personal data beyond what is described above.
				</Paragraph>

				<Title level={3} style={{ color: token.colorText }}>
					4. How We Use Your Information
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					We use your information to:
				</Paragraph>
				<ul style={{ color: token.colorTextSecondary, marginBottom: "16px" }}>
					<li>Provide and maintain our music streaming service.</li>
					<li>Manage your account and process subscriptions.</li>
					<li>Personalize your listening experience.</li>
					<li>
						Communicate with you about your account, updates, and support
						requests.
					</li>
					<li>Improve our services and develop new features.</li>
				</ul>

				<Title level={3} style={{ color: token.colorText }}>
					5. Data Sharing
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					We do not sell your personal information. We may share data with:
				</Paragraph>
				<ul style={{ color: token.colorTextSecondary, marginBottom: "16px" }}>
					<li>
						<strong>Stripe:</strong> For payment processing.
					</li>
					<li>
						<strong>Google:</strong> If you use Google Sign-In for
						authentication.
					</li>
					<li>
						<strong>AI service providers:</strong> Messages you send to our
						in-app assistant, together with limited account and listening
						context needed to answer you, are processed by third-party AI
						providers on our behalf to generate responses. These providers are
						not permitted to use your data for their own purposes.
					</li>
					<li>
						<strong>Law enforcement:</strong> When required by applicable law.
					</li>
				</ul>

				<Title level={3} style={{ color: token.colorText }}>
					6. Data Security
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					We implement appropriate technical and organizational measures to
					protect your personal data, including encrypted communications
					(HTTPS), secure authentication (JWT with RSA encryption), and access
					controls.
				</Paragraph>

				<Title level={3} style={{ color: token.colorText }}>
					7. Data Retention
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					We retain your personal data for as long as your account is active or
					as needed to provide our services. You can delete your account and
					associated data at any time from the Profile screen in the app, or by
					contacting us at support@bodhistreams.com.
				</Paragraph>

				<Title level={3} style={{ color: token.colorText }}>
					8. Your Rights
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					Depending on your jurisdiction, you may have the right to:
				</Paragraph>
				<ul style={{ color: token.colorTextSecondary, marginBottom: "16px" }}>
					<li>Access the personal data we hold about you.</li>
					<li>Request correction or deletion of your data.</li>
					<li>Object to or restrict the processing of your data.</li>
					<li>Request a portable copy of your data.</li>
				</ul>

				<Title level={3} style={{ color: token.colorText }}>
					9. Children's Privacy
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					Our service is not directed to children under the age of 13. We do not
					knowingly collect personal information from children under 13.
				</Paragraph>

				<Title level={3} style={{ color: token.colorText }}>
					10. Changes to This Policy
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					We may update this Privacy Policy from time to time. We will notify
					you of any changes by posting the new policy on this page and updating
					the "Last updated" date.
				</Paragraph>

				<Title level={3} style={{ color: token.colorText }}>
					11. Contact Us
				</Title>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					If you have any questions about this Privacy Policy, please contact us
					at:
				</Paragraph>
				<Paragraph style={{ color: token.colorTextSecondary }}>
					<strong>Email:</strong> support@bodhistreams.com
				</Paragraph>
			</div>
		</PageLayout>
	);
};

export default PrivacyPolicy;
